import { OrderEnum } from '@elearning-platform/api-types/enums';
import {
  Chip,
  DatesRange,
  FilterOption,
  PopupFilterType,
  SortingCategory,
  SortingDirection,
  useFormatDate,
  UseFormatDate,
  UseState,
} from '@elearning-platform/ui';
import dayjs from 'dayjs';
import { useState } from 'react';
import { TransProps, useTranslation } from 'react-i18next';

import { UseCommissionTableActions } from '../interfaces';

export const useCommissionTableActions: (
  initSortingValue?: string, initSortingDirection?: OrderEnum
) => UseCommissionTableActions = (
  initSortingValue?: string, initSortingDirection?: OrderEnum
): UseCommissionTableActions => {
  const [sortingDirection, setSortingDirection]: UseState<OrderEnum | undefined>
    = useState<OrderEnum | undefined>(initSortingDirection);
  const [sortingValue, setSortingValue]: UseState<string | undefined> = useState<string | undefined>(initSortingValue);
  const [searchValue, setSearchValue]: UseState<string> = useState<string>('');
  const [filters, setFilters]: UseState<Record<string, string[] | DatesRange>> = useState<Record<string, string[] | DatesRange>>({});
  const { formatDate, getTimestampMilisecValue }: UseFormatDate = useFormatDate();
  const getKeyForFiltersOptionsLabels: (filterName: string, filterOptionValue: string) => string = (
    filterName: string, filterOptionValue: string
  ): string => `${filterName}_${filterOptionValue}`;
  const { t }: TransProps<never> = useTranslation();

  const handleSortingChange: (category: SortingCategory) => void = (category: SortingCategory): void => {
    setSortingDirection(
      category.direction === SortingDirection.Asc
        ? OrderEnum.ASC
        : OrderEnum.DESC
    );
    setSortingValue(category.name);
  };

  const handleChipsChange: (chips: Chip[]) => void = (chips: Chip[]): void => {
    const result: Record<string, string[] | DatesRange> = {};

    for (const item of chips) {
      if (typeof item.value === 'string' && item.value.includes('_')) {
        const timestamps: string[] = item.value.split('_');
        result[item.parentName] = {
          dateFrom: timestamps[0].length ? dayjs(getTimestampMilisecValue(Number(timestamps[0]))) : undefined,
          dateTo: timestamps[1].length ? dayjs(getTimestampMilisecValue(Number(timestamps[1]))) : undefined,
        };
      } else {
        if (!result[item.parentName]) {
          result[item.parentName] = [];
        }
        const current: string[] | DatesRange = result[item.parentName];
        if (Array.isArray(current)) {
          current.push(item.value.toString());
        }
      }
    }

    setFilters(result);
  };

  const getFiltersOptionsLabels: (filters: PopupFilterType[]) => Record<string, string> = (
    filters: PopupFilterType[]
  ): Record<string, string> => filters.reduce<Record<string, string>>(
    (acc: Record<string, string>, item: PopupFilterType): Record<string, string> => {
      item.options?.forEach((option: FilterOption): void => {
        acc[getKeyForFiltersOptionsLabels(item.name, option.value)] = option.label;
      });

      return acc;
    },
    {}
  );

  const getInitialChips: (baseFilters: PopupFilterType[], chosenFilters: Record<string, string[] | DatesRange>) => Chip[] = (
    baseFilters: PopupFilterType[], chosenFilters: Record<string, string[] | DatesRange>,
  ): Chip[] => {
    const labelsMap: Record<string, string> = getFiltersOptionsLabels(baseFilters);
    const keysInDefinition: Set<string> = new Set(baseFilters.map((item: PopupFilterType): string => item.name));

    return Object.entries(chosenFilters)
      .flatMap(([key, values]: [string, string[] | DatesRange]): Chip[] => {
        if (keysInDefinition.has(key)) {
          const filterGroupLabel: string | undefined = baseFilters.find((filter: PopupFilterType): boolean => filter.name === key)?.label;
          if (Array.isArray(values)) {
            return values
              .filter((value: string): boolean => !!value)
              .map((value: string): Chip | null => {
                const label: string = `${filterGroupLabel}: ${labelsMap[getKeyForFiltersOptionsLabels(key, value)]}`;

                if (!label) {
                  return null;
                }

                return {
                  label,
                  value,
                  parentName: key,
                };
              })
              .filter((chip: Chip | null): chip is Chip => chip !== null);
          } else {
            let label: string = '';
            const dateFrom: number | undefined = values.dateFrom?.unix();
            const dateTo: number | undefined = values.dateTo?.unix();
            if (!!dateFrom && !!dateTo) {
              label = `${filterGroupLabel}: ${formatDate(dateFrom, 'DD.MM.YYYY')} - ${formatDate(dateTo, 'DD.MM.YYYY')}`;
            } else if (dateFrom) {
              label = `${filterGroupLabel} ${t(
                'competence.app.useCommissionTableActions.chips.dateFrom',
                { date: `${formatDate(dateFrom, 'DD.MM.YYYY')}` }
              )}`;
            } else if (dateTo) {
              label = `${filterGroupLabel} ${t(
                'competence.app.useCommissionTableActions.chips.dateTo',
                { date: `${formatDate(dateTo, 'DD.MM.YYYY')}` }
              )}`;
            } else {
              return [];
            }

            return [{
              label,
              value: `${dateFrom ?? ''}_${dateTo ?? ''}`,
              parentName: 'datesRange',
            }];
          }
        }

        return [];
      });
  };

  return {
    sortingDirection,
    sortingValue,
    searchValue,
    filters,
    setSortingDirection,
    setSortingValue,
    setSearchValue,
    setFilters,
    handleSortingChange,
    handleChipsChange,
    getInitialChips,
  };
};
